import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import moment from "moment";
import { fetchStoringen, fetchCategories } from "./Reducers/actions";
import { message } from "../../Reducers/loading/actions";

const StoringenPage = ({ 
  fetchStoringen, 
  fetchCategories,
  storingen, 
  categories 
}) => {
  const [loaded, setLoaded] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    prioriteit: '',
    categorie: '',
    page: 1,
    limit: 20,
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });

  useEffect(() => {
    const loadData = async () => {
      setLoaded(false);
      try {
        await Promise.all([
          fetchStoringen(filters),
          fetchCategories(false)
        ]);
        setLoaded(true);
      } catch (error) {
        console.error('Error loading storingen data:', error);
        setLoaded(true);
      }
    };

    loadData();
  }, [filters]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  const getPriorityColor = (prioriteit) => {
    switch (prioriteit) {
      case 'kritiek': return 'danger';
      case 'hoog': return 'warning';
      case 'normaal': return 'info';
      case 'laag': return 'success';
      default: return 'secondary';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'gemeld': return 'warning';
      case 'toegewezen': return 'info';
      case 'in_progress': return 'primary';
      case 'voltooid': return 'success';
      case 'geannuleerd': return 'secondary';
      default: return 'light';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'gemeld': return 'Gemeld';
      case 'toegewezen': return 'Toegewezen';
      case 'in_progress': return 'In Progress';
      case 'voltooid': return 'Voltooid';
      case 'geannuleerd': return 'Geannuleerd';
      default: return status;
    }
  };

  if (!loaded) {
    return (
      <div className="container-xxl flex-grow-1 container-p-y">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
          <div className="text-center">
            <div className="spinner-border text-primary mb-3" role="status">
              <span className="visually-hidden">Laden...</span>
            </div>
            <h6 className="text-muted">Storingen worden geladen...</h6>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-xxl flex-grow-1 container-p-y">
      {/* Page Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="fw-bold py-3 mb-2">
            <span className="text-muted fw-light">Facility Management /</span> Storingen
          </h4>
        </div>
        <div>
          <Link to="/facility/storingen/nieuw" className="btn btn-primary">
            <i className="bx bx-plus me-2"></i>Nieuwe Storing
          </Link>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h5 className="mb-0">Storingen Overzicht</h5>
        </div>
        
        {/* Filters */}
        <div className="card-body border-bottom">
          <div className="row g-3">
            <div className="col-md-3">
              <label className="form-label">Status</label>
              <select 
                className="form-select"
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <option value="">Alle statussen</option>
                <option value="gemeld">Gemeld</option>
                <option value="toegewezen">Toegewezen</option>
                <option value="in_progress">In Progress</option>
                <option value="voltooid">Voltooid</option>
                <option value="geannuleerd">Geannuleerd</option>
              </select>
            </div>
            
            <div className="col-md-3">
              <label className="form-label">Prioriteit</label>
              <select 
                className="form-select"
                value={filters.prioriteit}
                onChange={(e) => handleFilterChange('prioriteit', e.target.value)}
              >
                <option value="">Alle prioriteiten</option>
                <option value="kritiek">Kritiek</option>
                <option value="hoog">Hoog</option>
                <option value="normaal">Normaal</option>
                <option value="laag">Laag</option>
              </select>
            </div>
            
            <div className="col-md-3">
              <label className="form-label">Categorie</label>
              <select 
                className="form-select"
                value={filters.categorie}
                onChange={(e) => handleFilterChange('categorie', e.target.value)}
              >
                <option value="">Alle categorieën</option>
                {categories.map(cat => (
                  <option key={cat._id} value={cat._id}>
                    {cat.parent ? `${cat.parent.naam} > ${cat.naam}` : cat.naam}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="col-md-3">
              <label className="form-label">Sorteren op</label>
              <select 
                className="form-select"
                value={filters.sortBy}
                onChange={(e) => handleFilterChange('sortBy', e.target.value)}
              >
                <option value="createdAt">Datum aangemaakt</option>
                <option value="prioriteit">Prioriteit</option>
                <option value="status">Status</option>
                <option value="slaDeadline">SLA Deadline</option>
              </select>
            </div>
          </div>
        </div>

        <div className="card-body">
          {storingen.results && storingen.results.length > 0 ? (
            <>
              <div className="table-responsive text-nowrap">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th>Storingsnummer</th>
                      <th>Titel</th>
                      <th>Prioriteit</th>
                      <th>Status</th>
                      <th>Categorie</th>
                      <th>Accommodatie</th>
                      <th>Melder</th>
                      <th>Toegewezen aan</th>
                      <th>SLA Deadline</th>
                      <th>Aangemaakt</th>
                      <th>Acties</th>
                    </tr>
                  </thead>
                  <tbody className="table-border-bottom-0">
                    {storingen.results.map(storing => (
                      <tr key={storing._id} className={storing.isOverdue ? 'table-danger' : ''}>
                        <td>
                          <Link to={`/facility/storingen/${storing._id}`} className="fw-bold text-primary">
                            {storing.storingsNummer}
                          </Link>
                        </td>
                        <td>
                          <span className="fw-medium">{storing.titel}</span>
                        </td>
                        <td>
                          <span className={`badge bg-label-${getPriorityColor(storing.prioriteit)}`}>
                            {storing.prioriteit}
                          </span>
                          {storing.urgentie && (
                            <span className="badge bg-label-danger ms-1">
                              <i className="bx bx-error"></i>
                            </span>
                          )}
                        </td>
                        <td>
                          <span className={`badge bg-label-${getStatusColor(storing.status)}`}>
                            {getStatusLabel(storing.status)}
                          </span>
                        </td>
                        <td>
                          <span className="text-muted">
                            {storing.categorie ? storing.categorie.naam : 'Onbekend'}
                          </span>
                        </td>
                        <td>
                          <span className="text-muted">
                            {storing.accommodatie ? (
                              <>
                                <span className="badge bg-label-info me-1">Acc</span>
                                {storing.accommodatie.naam}
                              </>
                            ) : storing.kampeerplaats ? (
                              <>
                                <span className="badge bg-label-success me-1">KP</span>
                                {storing.kampeerplaats.naam}
                              </>
                            ) : (
                              'Geen'
                            )}
                          </span>
                        </td>
                        <td>
                          <span className="text-muted">
                            {storing.melder ? storing.melder.naam : 'Onbekend'}
                          </span>
                        </td>
                        <td>
                          {storing.toegewezenAan ? 
                            <span className="fw-medium">{storing.toegewezenAan.naam}</span> : 
                            <span className="text-muted">Niet toegewezen</span>
                          }
                        </td>
                        <td>
                          {storing.slaDeadline ? (
                            <span className={storing.isOverdue ? 'text-danger fw-bold' : 'text-muted'}>
                              {moment(storing.slaDeadline).format('DD-MM HH:mm')}
                              {storing.isOverdue && (
                                <i className="bx bx-time ms-1" title="SLA overschreden"></i>
                              )}
                            </span>
                          ) : <span className="text-muted">Geen deadline</span>}
                        </td>
                        <td>
                          <span className="text-muted">{moment(storing.createdAt).format('DD-MM HH:mm')}</span>
                        </td>
                        <td>
                          <div className="dropdown">
                            <button type="button" className="btn p-0 dropdown-toggle hide-arrow" data-bs-toggle="dropdown">
                              <i className="bx bx-dots-vertical-rounded"></i>
                            </button>
                            <div className="dropdown-menu">
                              <Link className="dropdown-item" to={`/facility/storingen/${storing._id}`}>
                                <i className="bx bx-show me-1"></i> Bekijk
                              </Link>
                              <Link className="dropdown-item" to={`/facility/storingen/${storing._id}/edit`}>
                                <i className="bx bx-edit me-1"></i> Bewerk
                              </Link>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {storingen.totalPages > 1 && (
                <div className="row">
                  <div className="col-md-6">
                    <p className="text-muted">
                      Toont {((filters.page - 1) * filters.limit) + 1} tot {Math.min(filters.page * filters.limit, storingen.total)} van {storingen.total} resultaten
                    </p>
                  </div>
                  <div className="col-md-6">
                    <nav>
                      <ul className="pagination justify-content-end">
                        <li className={`page-item ${filters.page <= 1 ? 'disabled' : ''}`}>
                          <button 
                            className="page-link"
                            onClick={() => handlePageChange(filters.page - 1)}
                            disabled={filters.page <= 1}
                          >
                            Vorige
                          </button>
                        </li>
                        
                        {Array.from({ length: Math.min(5, storingen.totalPages) }, (_, i) => {
                          const page = i + 1;
                          return (
                            <li key={page} className={`page-item ${filters.page === page ? 'active' : ''}`}>
                              <button 
                                className="page-link"
                                onClick={() => handlePageChange(page)}
                              >
                                {page}
                              </button>
                            </li>
                          );
                        })}
                        
                        <li className={`page-item ${filters.page >= storingen.totalPages ? 'disabled' : ''}`}>
                          <button 
                            className="page-link"
                            onClick={() => handlePageChange(filters.page + 1)}
                            disabled={filters.page >= storingen.totalPages}
                          >
                            Volgende
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center text-muted py-5">
              <i className="fas fa-inbox fa-4x mb-3"></i>
              <h4>Geen storingen gevonden</h4>
              <p>Er zijn geen storingen die voldoen aan de huidige filters.</p>
              <Link to="/facility/storingen/nieuw" className="btn btn-primary">
                <i className="fas fa-plus mr-2"></i>Eerste storing aanmaken
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  storingen: state.facilityManagement?.storingen || { results: [], totalPages: 0, currentPage: 1, total: 0 },
  categories: state.facilityManagement?.categories || []
});

const mapDispatchToProps = {
  fetchStoringen,
  fetchCategories,
  message
};

export default connect(mapStateToProps, mapDispatchToProps)(StoringenPage);